import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "./App.css";
import "react-toastify/dist/ReactToastify.css";

import MainView from "./components/Main/MainView";
import LoginPage from "./components/Login/Login";
import Header from "./components/Common/Header/Header";
import Footer from "./components/Common/Footer/Footer";
import ErrorPage from "./components/Error/Error";
import LogoutPage from "./components/Logout/Logout";
import SidebarNavigation from "./components/Common/SidebarNavigation";
import { NotFound, Redirect } from "./utility";
import * as C from "./components";
import { Box } from "@mui/material";
import { fetchAwsEvents } from "./Services";
import { fetchAliasAndMappings, loadReleaseData } from "./utility/releaseDataService";
import { vendorDict } from "./utility";
import AwsAlerts from "./components/AwsAlerts/AwsAlerts";

const getSessionEmail = () => {
  const raw = sessionStorage.getItem("email");
  return raw && raw !== "null" && raw !== "undefined" ? raw : null;
};
const getIsLoggedIn = () => sessionStorage.getItem("isLoggedIn") === "true";

function CalendarBootRedirect() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const target = sessionStorage.getItem("postBootTarget");
    if (pathname === "/calendar" && target) {
      setTimeout(() => navigate(target, { replace: true }), 0);
    }
  }, [pathname, navigate]);

  return null;
}

function AppLayout({ children, setPreferences }) {
  const location = useLocation();
  const isFullScreenPage = ["/login", "/error", "/logout"].includes(location.pathname);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", overflow: "hidden" }}>
      {!isFullScreenPage && (
        <Box
          className="position-fixed top-0 start-0 w-100 bg-white"
          sx={{ height: `55px`, zIndex: 10, borderBottom: "1px solid #ddd" }}
        >
          <Header setPreferences={setPreferences} />
        </Box>
      )}

      {!isFullScreenPage && (
        <Box
          className="position-fixed start-0"
          sx={{ top: `55px`, width: `72px`, height: `calc(100vh - 55px)`, backgroundColor: "#000", zIndex: 9 }}
        >
          <SidebarNavigation selectedTab={location.pathname.replace("/", "") || "calendar"} />
        </Box>
      )}

      <Box
        className="main-bg"
        sx={{
          flex: 1,
          marginTop: !isFullScreenPage ? "55px" : 0,
          marginLeft: !isFullScreenPage ? "72px" : 0,
          backgroundColor: "#f5f5f5",
        }}
      >
        {children}
      </Box>

      {!isFullScreenPage && <Footer />}
    </Box>
  );
}

function ProtectedRoute({ children }) {
  return children;
}

function RootRedirect({ mainViewState, preference }) {

  const storedView = sessionStorage.getItem("preferredView");
  if (storedView === "aws") return <Navigate to="/aws" replace />;
  if (storedView === "calendar") return <Navigate to="/calendar" replace />;

  if (preference?.view === "aws") return <Navigate to="/aws" replace />;
  if (preference?.view === "calendar") return <Navigate to="/calendar" replace />;

  if (mainViewState?.isAwsView === true) return <Navigate to="/aws" replace />;
  if (mainViewState?.isAwsView === false) return <Navigate to="/calendar" replace />;

  return <Navigate to="/calendar" replace />;
}

function App() {
  const INITIAL_MAINVIEW_STATE = {
    isAwsView: null,
    aliases: {},
    serviceObj: null,
    serviceComponents: {},
    dependentServices: {},
    sources: [],
    events: [],
    statusList: [],
    envList: [],
    tierList: [],
    hasServiceComponentReleases: false,
    hasDependentServiceReleases: false,
    isDataLoaded: false,
  };

  const [preference, setPreference] = useState(null);
  const setPreferences = (preferences) => {
    setPreference(preferences);
    if (preferences && preferences.view) {
      sessionStorage.setItem("preferredView", preferences.view);
    }
  };

  const [mainViewState, setMainViewState] = useState(INITIAL_MAINVIEW_STATE);
  const [loadString, setLoadString] = useState("");
  const [isNewUser, setIsNewUser] = useState(false);
  const [isLoading, setLoader] = useState(true);
  const [errorRedirect, setErrorRedirect] = useState(null);
  const [awsEvents, setAwsEvents] = useState(null);
  const [awsArchiveEvents, setAwsArchiveEvents] = useState(null);
  const [userType, setUserType] = useState(null);
  const [awsLastSyncedTime, setAwsLastSyncedTime] = useState(null);
  const [awsEventsLoader, setAwsLoader] = useState(true);
  const [awsProgress, setAwsProgress] = useState(null); 


  const email = getSessionEmail();

  useEffect(() => {
    if (!email) return;

    const PAGE_SIZE = 200;
    let cancelled = false;

    const loadActive = async () => {
      try {
        setAwsLoader(true);
        setAwsProgress(null);
        setAwsEvents(null);
        let cursor = 0;
        let hasMore = true;
        let aggregatedActive = {};
        let firstPageDone = false;
        let userTypeLocal = null;
        let lastSyncLocal = null;

        while (!cancelled && hasMore) {
          const res = await fetchAwsEvents(email, "active", {
            limit: PAGE_SIZE,
            cursor,
          });

          const body = res.data || {};
          const pageData = body.data || {};
          const pagination = body.pagination || null;

          userTypeLocal = body.usertype;
          lastSyncLocal = body.last_sync_time;

          aggregatedActive = { ...aggregatedActive, ...pageData };

          if (!firstPageDone) {
            setUserType(userTypeLocal);
            setAwsLastSyncedTime(lastSyncLocal);
            setAwsEvents(pageData);       // show first 50
            setAwsLoader(false);          // UI visible after first batch
            firstPageDone = true;
          } else {
            setAwsEvents((prev) => ({ ...(prev || {}), ...pageData }));
          }

          if (pagination && typeof pagination.total_accounts === "number") {
            const total = pagination.total_accounts;
            const loaded = Object.keys(aggregatedActive).length;
            let percentage = total ? Math.round((loaded / total) * 100) : 100;
            if (percentage > 100) percentage = 100;

            setAwsProgress({ loaded, total, percentage });
          }

          if (!pagination || !pagination.has_more) {
            hasMore = false;
          } else {
            cursor = pagination.next_cursor ?? 0;
          }
        }

        setAwsProgress((prev) =>
          prev && prev.percentage < 100 ? { ...prev, percentage: 100 } : prev
        );
      } catch (err) {
        console.error("Error fetching ACTIVE AWS Alerts:", err);
        setAwsLoader(false);
        setAwsProgress(null);
      }
    };

    loadActive();

    return () => {
      cancelled = true;
    };
  }, [email]);

  useEffect(() => {
    if (!email) return;

    if (userType !== "admin") {
      setAwsArchiveEvents(null);
      return;
    }

    const PAGE_SIZE = 200;
    let cancelled = false;

    const loadArchive = async () => {
      try {
        setAwsArchiveEvents(null);

        let cursor = 0;
        let hasMore = true;

        while (!cancelled && hasMore) {
          const res = await fetchAwsEvents(email, "archive", {
            limit: PAGE_SIZE,
            cursor,
          });

          const body = res.data || {};
          const pageData = body.data || {};
          const pagination = body.pagination || null;

          setAwsArchiveEvents((prev) => ({ ...(prev || {}), ...pageData }));

          if (!pagination || !pagination.has_more) {
            hasMore = false;
          } else {
            cursor = pagination.next_cursor ?? 0;
          }
        }
      } catch (err) {
        console.error("Error fetching ARCHIVED AWS Alerts:", err);
      }
    };

    loadArchive();

    return () => {
      cancelled = true;
    };
  }, [email, userType]);


  useEffect(() => {
    const run = async () => {
      if (!preference) {
        setMainViewState(INITIAL_MAINVIEW_STATE);
        return;
      }
      setMainViewState((prev) => ({ ...prev, isAwsView: preference["view"] === "aws" }));

      const serviceSet = new Set();
      const sourceSet = new Set();
      Object.keys(vendorDict).forEach((vendor) => {
        const key = vendorDict[vendor];
        if (key in preference && preference[key]) {
          sourceSet.add(vendor);
          preference[key].forEach((s) => serviceSet.add(s));
        }
      });

      const services = Array.from(serviceSet).sort();
      const newSources = Array.from(sourceSet);

      if (services.length <= 0) {
        setIsNewUser(true);
        setMainViewState(INITIAL_MAINVIEW_STATE);
        return;
      }

      try {
        const { aliases, mapping, components, dependents } = await fetchAliasAndMappings(services);
        setMainViewState((prev) => ({
          ...prev,
          aliases,
          serviceObj: mapping,
          serviceComponents: components,
          dependentServices: dependents,
          sources: newSources,
        }));

        const onProgress = (label) => setLoadString(label);
        const onPartial = ({ data }) => {
          setMainViewState((prev) => ({
            ...(prev || INITIAL_MAINVIEW_STATE),
            events: Array.isArray(data) ? data : [],
          }));
        };

        setLoader(false);
        const result = await loadReleaseData(
          { preference, sources: newSources, aliases, onPartial },
          onProgress
        );

        setMainViewState((prev) => ({
          ...prev,
          statusList: result.statusList,
          envList: result.envList,
          tierList: result.tierList,
          hasServiceComponentReleases: result.hasServiceComponentReleases,
          hasDependentServiceReleases: result.hasDependentServiceReleases,
          events: result.data,
          isDataLoaded: true,
        }));

        setIsNewUser(false);
        setTimeout(() => setLoadString("Data Loaded Successfully"), 300);
      } catch (err) {
        console.log(err?.message);
        if (
          err?.response?.status === 401 ||
          err?.response?.data?.message === "User authorization failed."
        ) {
          setErrorRedirect("logout");
        } else {
          setErrorRedirect("error");
        }
      }
    };

    run();
  }, [preference]);

  if (errorRedirect === "logout") return <Navigate to="/logout" replace />;
  if (errorRedirect === "error") return <Navigate to="/error" replace />;

  return (
    <>
      <CalendarBootRedirect />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/redirect" element={<Redirect />} />
        <Route path="/unsubscribe" element={<C.UnsubscribeView />} />
        <Route path="/subscribed-weekly-releases" element={<C.SubscribedReleases preference={preference} />} />
        <Route path="/subscribed-monthly-releases" element={<C.SubscribedReleases preference={preference} />} />
        <Route path="/subscribed-daily-releases" element={<C.SubscribedReleases preference={preference} />} />
        <Route path="/subscribed-instant-releases" element={<C.SubscribedReleases preference={preference} />} />

        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <AppLayout setPreferences={setPreferences}>
                <MainView
                  preference={preference}
                  mainViewState={mainViewState}
                  mainViewStateLoadString={loadString}
                  mainViewStateIsNewUser={isNewUser}
                  isLoading={isLoading}
                  setLoader={setLoader}
                />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/aws"
          element={
            <ProtectedRoute>
              <AppLayout setPreferences={setPreferences}>
                <AwsAlerts
                  userType={userType}
                  awsEvents={awsEvents}
                  awsArchiveEvents={awsArchiveEvents}
                  userEmail={email}
                  awsEventsLoader={awsEventsLoader}
                  lastSyncedTime={awsLastSyncedTime}
                  awsProgress={awsProgress}
                />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/" element={
          <AppLayout setPreferences={setPreferences}>
            <RootRedirect mainViewState={mainViewState} preference={preference} />
          </AppLayout>} />
        <Route
          path="/:releaseId"
          element={
            <ProtectedRoute>
              <AppLayout setPreferences={setPreferences}>
                <MainView
                  preference={preference}
                  mainViewState={mainViewState}
                  mainViewStateLoadString={loadString}
                  mainViewStateIsNewUser={isNewUser}
                  isLoading={isLoading}
                  setLoader={setLoader}
                />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/aws/:releaseId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AwsAlerts
                  userType={userType}
                  awsEvents={awsEvents}
                  awsArchiveEvents={awsArchiveEvents}
                  userEmail={email}
                  awsEventsLoader={awsEventsLoader}
                  lastSyncedTime={awsLastSyncedTime}
                  awsProgress={awsProgress}
                />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route path="/error" element={<ErrorPage />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

export default App;
